// src/utils/labOrders.js
//
// Lab tests on the Orders step. A visit stores them as ordinary next steps whose
// description starts with "Lab tests suggested:" (utils/labTestSuggestion.js is
// the contract with the patient screens, which offer a one-tap order link for
// such a step). The doctor edits a LIST of tests; this module folds that list
// into ONE lab-suggestion step and reads it back, so the visit payload and the
// patient's care plan stay exactly as before.

import {
  LAB_TEST_SUGGESTION_PREFIX, buildLabTestSuggestion, dueDateInWeeks, isLabTestSuggestion,
} from './labTestSuggestion';
import { DEFAULT_NEXT_STEP_PRIORITY, restrictPriority } from './consultation';

export const DEFAULT_LAB_WEEKS = 4;
export const MAX_RECOMMENDED_CHIPS = 5;

const lc = s => String(s || '').trim().toLowerCase();

/** Parse one lab-suggestion description → {markers, panelName, weeks, note}. */
export function parseLabDescription(description) {
  const rest = String(description || '').slice(LAB_TEST_SUGGESTION_PREFIX.length).trim();
  const out = { markers: [], panelName: '', weeks: null, note: '' };
  const repeat = rest.match(/Repeat in (\d+) weeks?\./);
  if (repeat) {
    out.weeks = Number(repeat[1]);
    out.note = rest.slice(repeat.index + repeat[0].length).trim();
  }
  const head = (repeat ? rest.slice(0, repeat.index) : rest).trim();
  const panel = head.match(/(?:^|\s)Panel: (.+?)\.?$/);
  let markerText = head;
  if (panel) { out.panelName = panel[1].trim(); markerText = head.slice(0, panel.index).trim(); }
  markerText = markerText.replace(/\.$/, '').trim();
  if (markerText) out.markers = markerText.split(',').map(s => s.trim()).filter(Boolean);
  return out;
}

/**
 * The lab order currently in `nextSteps`: all lab-suggestion steps merged.
 * @returns {{markers: string[], panelName: string, weeks: number, note: string, priority: string, dueDate: string, present: boolean}}
 */
export function labOrderFromSteps(nextSteps) {
  const labs = (nextSteps || []).filter(s => isLabTestSuggestion(s?.description));
  const merged = { markers: [], panelName: '', weeks: null, note: '', priority: DEFAULT_NEXT_STEP_PRIORITY, dueDate: '', present: labs.length > 0 };
  const seen = new Set();
  labs.forEach((s, i) => {
    const p = parseLabDescription(s.description);
    p.markers.forEach(m => { if (!seen.has(lc(m))) { seen.add(lc(m)); merged.markers.push(m); } });
    if (!merged.panelName && p.panelName) merged.panelName = p.panelName;
    if (merged.weeks == null && p.weeks != null) merged.weeks = p.weeks;
    if (!merged.note && p.note) merged.note = p.note;
    if (i === 0) { merged.priority = restrictPriority(s.priority); merged.dueDate = s.dueDate || ''; }
  });
  if (merged.weeks == null) merged.weeks = DEFAULT_LAB_WEEKS;
  return merged;
}

/** The non-lab next steps (the "Other instructions"), each with its index in `nextSteps`. */
export const instructionSteps = nextSteps =>
  (nextSteps || []).map((step, index) => ({ step, index })).filter(({ step }) => !isLabTestSuggestion(step?.description));

/**
 * Replace every lab-suggestion step by one built from `order` (placed where the
 * first one was, else appended). An order with no markers and no panel removes them.
 */
export function applyLabOrder(nextSteps, order) {
  const steps = nextSteps || [];
  const firstLab = steps.findIndex(s => isLabTestSuggestion(s?.description));
  const others = steps.filter(s => !isLabTestSuggestion(s?.description));
  const empty = !(order.markers || []).length && !order.panelName;
  if (empty) return others;
  const previous = labOrderFromSteps(steps);
  const weeks = order.weeks || DEFAULT_LAB_WEEKS;
  const weeksChanged = previous.present && previous.weeks !== weeks;
  const step = {
    description: buildLabTestSuggestion({ markers: order.markers || [], panelName: order.panelName || undefined, weeks, note: order.note }),
    priority: previous.present ? previous.priority : DEFAULT_NEXT_STEP_PRIORITY,
    dueDate: previous.present && previous.dueDate && !weeksChanged ? previous.dueDate : dueDateInWeeks(weeks),
  };
  const at = firstLab < 0 ? others.length : steps.slice(0, firstLab).filter(s => !isLabTestSuggestion(s?.description)).length;
  return [...others.slice(0, at), step, ...others.slice(at)];
}

export function addLabTest(nextSteps, name, extra = {}) {
  const t = String(name || '').trim();
  if (!t) return nextSteps;
  const order = labOrderFromSteps(nextSteps);
  if (order.markers.some(m => lc(m) === lc(t))) return nextSteps;
  return applyLabOrder(nextSteps, { ...order, markers: [...order.markers, t], ...extra });
}

export function removeLabTest(nextSteps, name) {
  const order = labOrderFromSteps(nextSteps);
  return applyLabOrder(nextSteps, { ...order, markers: order.markers.filter(m => lc(m) !== lc(name)) });
}

export function setLabWeeks(nextSteps, weeks) {
  const order = labOrderFromSteps(nextSteps);
  if (!order.present) return nextSteps;
  return applyLabOrder(nextSteps, { ...order, weeks });
}

/**
 * Search the recommended retests (and any panel names) for the combobox.
 * `options` = [{name, reason}], `selected` = names already added (hidden from results).
 */
export function filterTestOptions(options, query, selected = [], limit = 8) {
  const q = lc(query);
  const taken = new Set(selected.map(lc));
  return (options || [])
    .filter(o => o?.name && !taken.has(lc(o.name)) && (!q || lc(o.name).includes(q)))
    .slice(0, limit);
}

/** At most `max` recommended chips, none already added. */
export function topRecommended(options, selected = [], max = MAX_RECOMMENDED_CHIPS) {
  const taken = new Set(selected.map(lc));
  return (options || []).filter(o => o?.name && !taken.has(lc(o.name))).slice(0, max);
}
